(function () {
  if (window.GBVideoShop?.booted) {
    window.GBVideoShop.init(document);
    return;
  }

  const ROOT_SELECTOR = '[data-gb-vshop]';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  class VideoShop {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('.gb-vshop__grid');
      this.videos = Array.from(root.querySelectorAll('.gb-vshop__video'));
      this.cards = Array.from(root.querySelectorAll('.gb-vshop__card'));
      this.addButtons = Array.from(root.querySelectorAll('[data-add-to-cart]'));
      this.autoSlideEnabled = String(root.dataset.autoSlide) === 'true';
      this.autoSlideSpeedMs = (parseInt(root.dataset.autoSlideSpeed || '4', 10) || 4) * 1000;
      this.autoSlideTimer = 0;
      this.autoSlidePaused = false;
    }

    init() {
      this.onMediaPlay = this.onMediaPlay.bind(this);
      this.onMediaPause = this.onMediaPause.bind(this);
      this.onAddClick = this.onAddClick.bind(this);
      this.onSliderPointerDown = this.onSliderPointerDown.bind(this);
      this.onSliderPointerUp = this.onSliderPointerUp.bind(this);
      this.onSliderMouseEnter = this.onSliderMouseEnter.bind(this);
      this.onSliderMouseLeave = this.onSliderMouseLeave.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onResize = this.onResize.bind(this);

      this.videos.forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        video.loop = true;
        video.autoplay = true;
        video.addEventListener('play', this.onMediaPlay);
        video.addEventListener('pause', this.onMediaPause);
        video.play().catch(() => {});
      });

      this.addButtons.forEach((button) => {
        button.addEventListener('click', this.onAddClick);
      });

      if (this.autoSlideEnabled && this.grid) {
        this.grid.addEventListener('pointerdown', this.onSliderPointerDown, { passive: true });
        window.addEventListener('pointerup', this.onSliderPointerUp, { passive: true });
        this.grid.addEventListener('mouseenter', this.onSliderMouseEnter);
        this.grid.addEventListener('mouseleave', this.onSliderMouseLeave);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('resize', this.onResize, { passive: true });
        this.startAutoSlide();
      }

      this.syncPlaybackStates();
    }

    destroy() {
      this.stopAutoSlide();

      this.videos.forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        video.removeEventListener('play', this.onMediaPlay);
        video.removeEventListener('pause', this.onMediaPause);
      });

      this.addButtons.forEach((button) => {
        button.removeEventListener('click', this.onAddClick);
      });

      this.grid?.removeEventListener('pointerdown', this.onSliderPointerDown);
      window.removeEventListener('pointerup', this.onSliderPointerUp);
      this.grid?.removeEventListener('mouseenter', this.onSliderMouseEnter);
      this.grid?.removeEventListener('mouseleave', this.onSliderMouseLeave);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onResize);
    }

    onSliderPointerDown() {
      this.autoSlidePaused = true;
      this.stopAutoSlide();
    }

    onSliderPointerUp() {
      this.autoSlidePaused = false;
      this.startAutoSlide();
    }

    onSliderMouseEnter() {
      this.autoSlidePaused = true;
      this.stopAutoSlide();
    }

    onSliderMouseLeave() {
      this.autoSlidePaused = false;
      this.startAutoSlide();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoSlide();
        return;
      }
      this.startAutoSlide();
    }

    onResize() {
      this.startAutoSlide();
    }

    stopAutoSlide() {
      window.clearTimeout(this.autoSlideTimer);
    }

    isAutoSlideEligible() {
      if (!this.autoSlideEnabled || !this.grid || this.cards.length < 2) return false;
      if (reducedMotionQuery.matches) return false;
      if (!window.matchMedia('(max-width: 749px)').matches) return false;
      return this.grid.scrollWidth - this.grid.clientWidth > 4;
    }

    getSlideStep() {
      if (!this.grid) return 0;
      if (this.cards.length > 1) {
        return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
      }
      return this.cards[0]?.offsetWidth || this.grid.clientWidth * 0.75;
    }

    slideNext() {
      if (!this.grid) return;
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth - 1;
      const behavior = reducedMotionQuery.matches ? 'auto' : 'smooth';
      const atEnd = this.grid.scrollLeft >= maxScroll;

      if (atEnd) {
        this.grid.scrollTo({ left: 0, behavior });
        return;
      }

      this.grid.scrollBy({ left: this.getSlideStep(), behavior });
    }

    startAutoSlide() {
      this.stopAutoSlide();
      if (!this.isAutoSlideEligible() || this.autoSlidePaused || document.hidden) return;

      this.autoSlideTimer = window.setTimeout(() => {
        this.slideNext();
        this.startAutoSlide();
      }, this.autoSlideSpeedMs);
    }

    onMediaPlay(event) {
      const current = event.currentTarget;
      this.ensureLooping(current);
    }

    onMediaPause(event) {
      const current = event.currentTarget;
      this.ensureLooping(current);
    }

    syncPlaybackStates() {
      this.videos.forEach((video) => this.ensureLooping(video));
    }

    ensureLooping(video) {
      if (!(video instanceof HTMLVideoElement)) return;
      video.loop = true;
      if (video.paused) {
        video.play().catch(() => {});
      }
    }

    async onAddClick(event) {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.disabled) return;

      const variantId = parseInt(button.dataset.variantId || '', 10);
      if (!variantId) {
        this.showError(button);
        return;
      }

      const previousDisabled = button.disabled;

      button.disabled = true;

      try {
        const addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!addResponse.ok) {
          throw new Error('Add to cart request failed');
        }

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!cartResponse.ok) {
          throw new Error('Cart refresh failed');
        }

        const cart = await cartResponse.json();
        this.updateCartCount(cart.item_count || 0);
        this.showSuccess(button);
      } catch (error) {
        this.showError(button);
      } finally {
        button.disabled = previousDisabled;
      }
    }

    showSuccess(button) {
      button.classList.add('is-success');

      window.setTimeout(() => {
        button.classList.remove('is-success');
      }, 1500);
    }

    showError(button) {
      if (reducedMotionQuery.matches) return;

      button.classList.remove('is-error');
      void button.offsetWidth;
      button.classList.add('is-error');
      window.setTimeout(() => {
        button.classList.remove('is-error');
      }, 400);
    }

    updateCartCount(itemCount) {
      const countText = itemCount > 99 ? '99+' : String(itemCount);
      const selectors = [
        '[data-cart-count]',
        '.cart-count-bubble',
        '.header__icon--cart .count',
        '.cart-bubble',
        '.cart-bubble__text-count',
        '[data-testid="cart-bubble"]',
      ];

      document.querySelectorAll(selectors.join(',')).forEach((node) => {
        if (node.classList.contains('cart-bubble')) {
          node.classList.toggle('visually-hidden', itemCount === 0);
          const countNode = node.querySelector('.cart-bubble__text-count, [data-testid="cart-bubble"]');
          if (countNode) {
            countNode.textContent = countText;
            countNode.classList.toggle('hidden', itemCount === 0);
          }
          return;
        }

        node.textContent = countText;
      });
    }
  }

  function init(scope) {
    const container = scope || document;
    container.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      const app = new VideoShop(root);
      app.init();
      instances.set(root, app);
    });
  }

  function unload(event) {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (!root) return;
    const app = instances.get(root);
    if (!app) return;
    app.destroy();
    instances.delete(root);
  }

  function load(event) {
    init(event.target);
  }

  window.GBVideoShop = {
    booted: true,
    init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', load);
  document.addEventListener('shopify:section:unload', unload);
})();
